import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { reviewOwnLetter, reviewOwnMedia } from '../lib/vault/ownerReviewApi';
import {
  getMyVault, listMessages, rescheduleMessage, withdrawMessage, deleteMessage,
  type MessageListItem,
} from '../lib/vault/vaultApi';
import './vault.css';

/**
 * Every message the owner has recorded, in one place. The vault is theirs: over
 * time they'll want to revisit, reschedule, take one back, or remove one they no
 * longer want at all — so they should always be able to see the whole set, find a
 * person, and act. Grouped by recipient by default; searchable as the vault fills.
 */
export default function AllMessagesScreen() {
  const { api } = useAuth();
  const [messages, setMessages] = useState<MessageListItem[] | null>(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<'person' | 'recent'>('person');
  const [vaultId, setVaultId] = useState<string | null>(null);

  async function load() {
    setError('');
    try {
      const dash = await getMyVault(api);
      setVaultId(dash.vault.id);
      const { messages: m } = await listMessages(api, dash.vault.id);
      setMessages(m);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your messages.');
    }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    if (!messages) return [];
    const q = query.trim().toLowerCase();
    const rows = q
      ? messages.filter((m) =>
          (m.title ?? '').toLowerCase().includes(q) ||
          (m.recipient_full_name ?? '').toLowerCase().includes(q) ||
          m.format.toLowerCase().includes(q))
      : messages;
    return rows;
  }, [messages, query]);

  // Group by recipient (person sort) or flat by created_at (recent sort).
  const groups = useMemo(() => {
    if (sort === 'recent') {
      return [{ name: null as string | null, items: filtered }];
    }
    const byPerson = new Map<string, { name: string; items: MessageListItem[] }>();
    for (const m of filtered) {
      const key = m.recipient_id ?? '—';
      const name = m.recipient_full_name ?? 'No recipient';
      if (!byPerson.has(key)) byPerson.set(key, { name, items: [] });
      byPerson.get(key)!.items.push(m);
    }
    return [...byPerson.values()].sort((a, b) => a.name.localeCompare(b.name)).map((g) => ({ name: g.name, items: g.items }));
  }, [filtered, sort]);

  if (error) {
    return (
      <section className="vault">
        <p role="alert" className="vault__alert">{error}</p>
        <p className="vault__alt"><Link className="la-btn-secondary" to="/">Back to vault</Link></p>
      </section>
    );
  }
  if (messages === null) return <section className="vault"><p role="status" className="vault__kicker">Opening…</p></section>;

  const total = messages.filter((m) => m.status !== 'withdrawn').length;

  return (
    <section className="vault">
      <p className="vault__kicker">Your vault</p>
      <h1 className="vault__title">Everything you've recorded.</h1>
      <p className="vault__sub">{total} live {total === 1 ? 'message' : 'messages'}{messages.length > total ? ` · ${messages.length - total} withdrawn` : ''}</p>

      {messages.length > 0 && (
        <div className="mfilter">
          <input className="auth__input mfilter__search" type="search" placeholder="Search by person, title, or format" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search messages" />
          <div className="mfilter__sort" role="group" aria-label="Sort">
            <button className="la-btn-secondary" aria-pressed={sort === 'person'} onClick={() => setSort('person')}>By person</button>
            <button className="la-btn-secondary" aria-pressed={sort === 'recent'} onClick={() => setSort('recent')}>Most recent</button>
          </div>
        </div>
      )}

      {messages.length === 0 ? (
        <p className="vault__empty">Nothing recorded yet. When you seal a message, it will live here.</p>
      ) : filtered.length === 0 ? (
        <p className="vault__empty">No messages match "{query}".</p>
      ) : (
        groups.map((g, i) => (
          <div key={g.name ?? i} className="mgroup">
            {g.name !== null && <p className="vault__section-label">{g.name} · {g.items.length}</p>}
            <div className="mlist">
              {g.items.map((m) => (
                <MessageCard key={m.id} m={m} showRecipient={sort === 'recent'} onChanged={() => void load()} vaultId={vaultId} />
              ))}
            </div>
          </div>
        ))
      )}
      <p className="vault__alt" style={{ marginTop: 28 }}><Link className="la-btn-secondary" to="/">Back to vault</Link></p>
    </section>
  );
}

function whenLabel(m: MessageListItem): string {
  const t = m.trigger;
  if (m.status === 'delivered') return `Delivered${m.delivered_at ? ' ' + new Date(m.delivered_at).toLocaleDateString() : ''}`;
  if (m.status === 'withdrawn') return 'Withdrawn';
  // A sealed message with no live trigger will never deliver — say so honestly
  // rather than the old misleading "Scheduled" (the sealed-orphan sweep flags it).
  if ((m.trigger_count ?? 0) === 0 && (m.status === 'sealed' || m.status === 'draft')) {
    return 'No delivery time set';
  }
  // A compound schedule has multiple legs; the summary carries only one, so don't
  // pretend a single condition is the whole picture.
  if ((m.trigger_count ?? 0) > 1) return 'Arrives on the conditions you set';
  if (t.type === 'date' && t.trigger_date) return `Arrives ${new Date(t.trigger_date).toLocaleDateString()}`;
  if (t.type === 'age' && t.target_age != null) return `Arrives when they turn ${t.target_age}`;
  if ((t.type === 'life_event' || t.type === 'inheritance') && t.life_event_type) return `Arrives at: ${t.life_event_type.replace(/_/g, ' ')}`;
  if (t.type === 'emergency_only') return 'Held until you are gone';
  return 'Scheduled';
}

function MessageCard({ m, showRecipient, onChanged, vaultId }: { m: MessageListItem; showRecipient: boolean; onChanged: () => void; vaultId: string | null }) {
  const { api, user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<'idle' | 'reschedule' | 'confirmDelete' | 'review'>('idle');
  const [newDate, setNewDate] = useState('');
  const [letter, setLetter] = useState<string | null>(null);
  const [media, setMedia] = useState<{ url: string; mimeType: string } | null>(null);
  const canChange = m.status !== 'delivered' && m.status !== 'withdrawn';
  const today = new Date().toISOString().slice(0, 10);

  // You can reopen what you recorded, right up until it is delivered. After
  // that it belongs to the person you sent it to, and taking it back is not
  // ours to offer. Withdrawn messages stay closed too — the member already
  // decided not to send that one.
  const canReview = vaultId !== null && m.status !== 'delivered' && m.status !== 'withdrawn' && m.status !== 'draft';

  // Blob URLs for a decrypted recording are revoked on close — the plaintext
  // should not outlive the moment the member is looking at it.
  function closeReview() {
    if (media !== null) URL.revokeObjectURL(media.url);
    setMedia(null); setLetter(null); setMode('idle'); setError('');
  }

  async function openReview() {
    if (vaultId === null || user?.email === undefined) return;
    setBusy(true); setError(''); setMode('review');
    try {
      if (m.format === 'letter') {
        setLetter(await reviewOwnLetter({ api, vaultId, messageId: m.id, ownerEmail: user.email }));
      } else {
        setMedia(await reviewOwnMedia({ api, vaultId, messageId: m.id, ownerEmail: user.email }));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open this message.');
      setMode('idle');
    } finally { setBusy(false); }
  }

  async function run(fn: () => Promise<unknown>, fail: string) {
    setBusy(true); setError('');
    try { await fn(); onChanged(); }
    catch (e) { setError(e instanceof Error ? e.message : fail); setBusy(false); }
  }

  return (
    <div className={`mcard${m.status === 'withdrawn' ? ' mcard--withdrawn' : ''}`}>
      <div className="mcard__body">
        <div className="mcard__title">{m.title?.trim() || `A ${m.format} message`}</div>
        <div className="mcard__meta">
          {showRecipient && m.recipient_full_name ? `${m.recipient_full_name} · ` : ''}{whenLabel(m)}{m.is_emergency_only ? ' · emergency-only' : ''}
        </div>
        {error && <div className="rrow__error" role="alert">{error}</div>}
        {mode === 'reschedule' && (
          <div className="mcard__edit">
            <input className="auth__input" type="date" min={today} value={newDate} onChange={(e) => setNewDate(e.target.value)} aria-label="New delivery date" />
            <button className="la-btn-primary" disabled={busy || !newDate} onClick={() => void run(() => rescheduleMessage(api, m.id, { trigger_date: newDate }), 'Could not reschedule.')}>{busy ? 'Saving…' : 'Save date'}</button>
            <button className="la-btn-secondary" disabled={busy} onClick={() => { setMode('idle'); setError(''); }}>Cancel</button>
          </div>
        )}
        {mode === 'review' && (
          <div className="mcard__review">
            {busy && <p className="vault__sub">Opening…</p>}
            {letter !== null && <p className="mcard__letter">{letter}</p>}
            {media !== null && (
              media.mimeType.startsWith('audio')
                ? <audio className="mcard__media" src={media.url} controls />
                : <video className="mcard__media" src={media.url} controls playsInline />
            )}
            {(letter !== null || media !== null) && (
              <button className="la-btn-secondary" onClick={closeReview}>Close</button>
            )}
          </div>
        )}
        {mode === 'confirmDelete' && (
          <div className="mcard__edit">
            <span className="mcard__confirm">Delete this message for good?</span>
            <button className="la-btn-primary" disabled={busy} onClick={() => void run(() => deleteMessage(api, m.id), 'Could not delete.')}>{busy ? 'Deleting…' : 'Yes, delete'}</button>
            <button className="la-btn-secondary" disabled={busy} onClick={() => { setMode('idle'); setError(''); }}>Keep it</button>
          </div>
        )}
      </div>
      {mode === 'idle' && (
        <div className="mcard__actions">
          {canReview && <button className="la-btn-secondary" disabled={busy} onClick={() => void openReview()}>Review</button>}
          {canChange && <button className="la-btn-secondary" onClick={() => setMode('reschedule')}>Reschedule</button>}
          {canChange && <button className="la-btn-secondary" disabled={busy} onClick={() => void run(() => withdrawMessage(api, m.id), 'Could not withdraw.')}>Withdraw</button>}
          {m.status !== 'delivered' && <button className="la-btn-secondary mcard__danger" onClick={() => setMode('confirmDelete')}>Delete</button>}
        </div>
      )}
    </div>
  );
}
